"use client";

import React, { memo, useMemo, useCallback, useState } from "react";
import { ExternalLinkIcon } from "lucide-react";
import { PreviewDialog } from "./preview-dialog";
import { FileSystemItem } from "types";
import { useTranslation } from "react-i18next";

interface SourceMessagePartProps {
  sourceType: string;
  id: string;
  url: string;
  title?: string; // may contain a plain string OR JSON-serialized reference object
  parentId?: string;
}

// Narrow type for the parsed reference (adjust to your real shape as needed)
type ParsedRef = {
  id?: string;
  path?: string;
  name?: string;
  reference_document_id?: string;
  reference_metadata?: Array<{
    page_number: number;
    content?: string;
    similarity_score?: number;
  }>;
  metadata?: {
    original_filename?: string;
    [k: string]: any;
  };
  pages?: Array<{
    page_number: number;
    content?: string;
    similarity_score?: number;
  }>;
  [k: string]: any;
};

const isLikelyJson = (s?: string) =>
  !!s && (s.trim().startsWith("{") || s.trim().startsWith("["));

/**
 * Parse the title into a reference object if it contains JSON; otherwise return null.
 */
const parseTitleToRef = (title?: string): ParsedRef | null => {
  console.log(title);
  if (!title || !isLikelyJson(title)) return null;
  try {
    return JSON.parse(title) as ParsedRef;
    } catch {
      return null;
  }
};

/**
 * Build a user-friendly display name from a parsed ref.
 */
const getDisplayNameFromRef = (
  ref: ParsedRef | null,
  fallback?: string
): string => {
  if (!ref) return fallback || "";
  return ref.name || ref.metadata?.original_filename || fallback || "";
};

/**
 * Extract page numbers from the parsed ref (if present).
 */
const getPagesFromRef = (ref: ParsedRef | null): number[] => {
  if (!ref?.pages?.length) return [];
  return ref.pages
    .map((p) => p?.page_number)
    .filter((n): n is number => Number.isFinite(n));
};

/**
 * Add a page hint to URLs if possible (supports typical PDF viewers).
 * Uses "#page=N" for PDFs; otherwise appends "?page=N" (or "&page=N").
 */
const buildUrlForPage = (baseUrl: string, page?: number) => {
  if (!page) return baseUrl;

  const lower = baseUrl.toLowerCase();
  const isPdf = lower.endsWith(".pdf") || lower.includes(".pdf?");
  if (isPdf) {
    // preserve existing hash if any
    const [pure, hash = ""] = baseUrl.split("#");
    const withoutPage = hash.replace(/(^|&)page=\d+/i, "");
    const sep = withoutPage.length ? "&" : "";
    return `${pure}#${withoutPage}${sep}page=${page}`;
  }

  try {
    const u = new URL(
      baseUrl,
      typeof window !== "undefined" ? window.location.href : undefined
    );
    u.searchParams.set("page", String(page));
    return u.toString();
  } catch {
    // Fallback: append as a query param
    return baseUrl.includes("?")
      ? `${baseUrl}&page=${page}`
      : `${baseUrl}?page=${page}`;
  }
};

const SourceMessagePart = memo<SourceMessagePartProps>((option) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogRef, setDialogRef] = useState<ParsedRef | null>(null);
  const [dialogPage, setDialogPage] = useState<number | null>(null);
  const [fileToShow, setFileToShow] = useState<FileSystemItem | null>(null);

  const { sourceType, id, url, title, parentId } = option;

  // Parse reference (if title contains JSON)
  const parsedRefOrArray = useMemo(() => parseTitleToRef(title), [title]);

  const isRefArray = Array.isArray(parsedRefOrArray);

  // Convert single reference to array format and deduplicate
  const references: ParsedRef[] = useMemo(() => {
    if (isRefArray) {
      return parsedRefOrArray as ParsedRef[];
    }
    
    if (!parsedRefOrArray) {
      return [];
    }

    // Create proper reference object from parsed data
    const ref: ParsedRef = {
      id: parsedRefOrArray.reference_document_id || id,
      path: parsedRefOrArray.reference_document_id || url,
      name: parsedRefOrArray?.reference_document_name || 'File',
      metadata: parsedRefOrArray.metadata || {},
      pages: parsedRefOrArray.reference_metadata?.map((meta: any) => ({
        page_number: meta.page_number,
        content: meta.content,
        similarity_score: meta.similarity_score
      })) || []
    };

    return [ref];
  }, [parsedRefOrArray, isRefArray, id, url, title]);

  // Deduplicate references by ID and name
  const uniqueReferences = useMemo(() => {
    const seen = new Set<string>();
    return references.filter((ref) => {
      const key = `${ref.id || ''}-${ref.name || ''}-${ref.path || ''}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [references]);
  
  const mainRef = uniqueReferences[0] || null;

  console.log(mainRef);
  // Derived display values
  const displayName = useMemo(
    () => getDisplayNameFromRef(mainRef, title || url),
    [mainRef, title, url]
  );

  const pageNumbers = useMemo(() => getPagesFromRef(mainRef), [mainRef]);

  const handleOpenDialog = (ref: ParsedRef, page: number | null) => {
    setDialogRef(ref);
    setDialogPage(page);
    setDialogOpen(true);
    setFileToShow({
      id: ref.id,
      name: ref.name,
      created_at: ref.metadata?.uploaded_at || "",
      updated_at: ref.metadata?.uploaded_at || "",
      agent_document_id: undefined,
      parent_id: null,
      workspace_id: "",
      path: ref.path,
      document_metadata: {
        file_size: ref.metadata?.file_size || 0,
        file_type: ref.metadata?.file_type || "pdf",
        original_filename: ref.metadata?.original_filename || "",
        uploaded_at: ref.metadata?.uploaded_at || "",
      },
      type: "Document",
    });
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setTimeout(() => {
      setDialogRef(null);
      setDialogPage(null);
      setFileToShow(null);
    }, 200); // delay to allow for closing animation
  };

  // Open URL (optionally at a page) AND fire onReferenceClick with the parsed object
  const openAndNotify = useCallback(
    (ref: ParsedRef, page: number | null = null) => {
      // Fire callback first (so UI can react immediately)
      const payload = page ? { ...ref, selectedPage: page } : ref;
      handleOpenDialog(payload, page);
    },
    []
  );

  // Primary title click: open URL and notify with the full ref (no specific page)
  const handlePrimaryClick = useCallback(() => {
    if (mainRef) {
      openAndNotify(mainRef);
    }
  }, [openAndNotify, mainRef]);

  // Only show if we have valid references
  if (!sourceType || !id || !url || uniqueReferences.length === 0) {
    return null;
  }

  return (
    <>
      <div className="rounded-lg border border-border bg-card p-3">
        <div className="space-y-2">
          {uniqueReferences.map((ref, idx) => (
            <div
              key={`${ref.id || ref.name || "ref"}-${ref.path || ""}-${idx}`}
              className="flex items-center gap-1 text-xs"
            >
              <button
                onClick={() => openAndNotify(ref, null)}
                className="hover:text-primary/80 font-medium text-primary transition-colors duration-200"
              >
                {ref.name || "Untitled"}
              </button>

              {ref.pages && ref.pages.length > 0 && (
                <>
                  <span className="text-muted-foreground">• Pages: </span>
                  <div className="flex flex-wrap gap-1">
                    {ref.pages.map((p: any, pidx) => (
                      <button
                        key={`${ref.id}-page-${p.page_number}-${pidx}`}
                        onClick={() => openAndNotify(ref, p.page_number)}
                        title={`View page ${p.page_number}`}
                        className="hover:text-primary/80 text-primary underline transition-colors duration-200"
                      >
                        {p.page_number || p.number}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
      <PreviewDialog
        isOpen={dialogOpen && !!dialogRef}
        setIsOpen={setDialogOpen}
        onClose={handleCloseDialog}
        file={fileToShow}
        page={dialogPage}
      />
    </>
  );
});

SourceMessagePart.displayName = "SourceMessagePart";

export { SourceMessagePart };
